/**
 * Offline capability grant verification (Phase 8, ADR-009 §2).
 *
 * A grant is a `jose` JWS (ES256, asymmetric) issued by the server
 * (`apps/api/src/modules/auth/offlineGrant.ts`) for a currently-authenticated
 * session and verified here, locally, with no network call - the device
 * holds only the PUBLIC key (pinned once, while online, via
 * `GET /api/auth/offline-grant/public-key`), so nothing that leaks from the
 * device can be used to forge a grant. This is deliberately asymmetric,
 * unlike the existing access token's symmetric `JWT_SECRET` HS256
 * (`apps/api/src/modules/auth/tokens.ts`), because that token is only ever
 * verified server-side - a grant must be verifiable *offline*, by
 * definition, so the verifying party cannot be trusted with the signing
 * secret.
 */
import { importSPKI, jwtVerify } from 'jose';
import { offlineGrantPayloadSchema } from '@kortex/shared';
export class OfflineGrantError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'OfflineGrantError';
    }
}
/**
 * Verifies a grant's signature and expiry against the pinned public key.
 * Throws `OfflineGrantError` rather than returning null/undefined, so a
 * caller cannot accidentally treat "grant invalid" as "grant absent" -
 * these need different UI treatment (re-enrol vs. just go online once).
 */
export async function verifyOfflineGrant(token, publicKeySpki, now = new Date()) {
    let publicKey;
    try {
        publicKey = await importSPKI(publicKeySpki, 'ES256');
    }
    catch {
        throw new OfflineGrantError('MALFORMED', 'Offline grant public key is not a valid ES256 SPKI key');
    }
    let payload;
    try {
        ({ payload } = await jwtVerify(token, publicKey, { currentDate: now }));
    }
    catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        if (message.toLowerCase().includes('exp')) {
            throw new OfflineGrantError('EXPIRED', 'Offline grant has expired');
        }
        throw new OfflineGrantError('INVALID_SIGNATURE', 'Offline grant signature is invalid');
    }
    const parsed = offlineGrantPayloadSchema.safeParse(payload);
    if (!parsed.success || typeof payload.exp !== 'number' || typeof payload.iat !== 'number') {
        throw new OfflineGrantError('MALFORMED', 'Offline grant payload is missing required fields');
    }
    return { ...parsed.data, notAfter: payload.exp, issuedAt: payload.iat };
}
/** Fast, jose-free pre-check - use before ever touching `jose`/crypto. */
export function isGrantExpired(notAfterEpochSeconds, now = new Date()) {
    return now.getTime() >= notAfterEpochSeconds * 1000;
}
//# sourceMappingURL=grant.js.map